<template>
  <v-card flat
    class="mb-2"
    :class="this.cardClass()"
  >
    <div class="d-flex justify-space-between align-center">
      <div>
        <v-card-title class="pa-1 list-card-title">
          <mrx-translation :token="name"></mrx-translation>
        </v-card-title>
        <v-card-subtitle class="pa-1 pt-2 list-card-subtitle">
          <span
            v-for="response in selectedResponses"
            :key="response.id"
            subheading
            :id="responseId(response)"
            >
            <mrx-translation :showUntranslatedToken="true" :token="formatResponse(response)" />
          </span>
        </v-card-subtitle>
      </div>
      <div
        >
        <v-btn
          :id="'edit-response-' + item.name"
          class="mt-2"
          @click="editResponse"
          v-if="!item.computation && !item.hideEdit"
          color="var(--deep-blue)"
          icon>
          <v-icon>{{ icons.mdiPencil }}</v-icon>
        </v-btn>
      </div>
    </div>
  </v-card>
</template>

<script>
  import icons from '@/mixins/icons'
  import { convertFromKilograms, convertFromCelsius, convertFromCm } from '@/utils/conversions'
  import ageRanges from '@/mixins/ageRanges'
  import displayFormats from '@/mixins/displayFormats'
  import languageLogic from '@/mixins/languageLogic'
  import tokens from '@/mixins/tokens'
  import ui from '@/mixins/ui'

  let capitalize = (value) => {
    if (!value) return ''
    value = value.toString()
    return value.charAt(0).toUpperCase() + value.slice(1)
  }

  const weightConversions = (useImperialUnits) => {
    const imperialUnitsName = 'pounds'
    if (useImperialUnits) {
      return {
        units: imperialUnitsName,
        conversion: (value) => convertFromKilograms(imperialUnitsName, 1, value)
      }
    }

    return {
      units: 'kilograms',
      conversion: (value) => value,
    }
  }

  const temperatureConversion = (useImperialUnits) => {
    const imperialUnitsName = 'fahrenheit'
    if (useImperialUnits) {
      return {
        units: imperialUnitsName,
        conversion: (value) => convertFromCelsius(imperialUnitsName, 1, 1, value)
      }
    }

    return {
      units: 'celsius',
      conversion: (value) => value,
    }
  }

  const heightConversion = (useImperialUnits) => {
    const imperialUnitsName = 'inches'
    if (useImperialUnits) {
      return {
        units: imperialUnitsName,
        conversion: (value) => convertFromCm(imperialUnitsName, 1, value)
      }
    }

    return {
      units: 'centimeters',
      conversion: (value) => value,
    }
  }

  const getConversions = (useImperialUnits) => ({
    weight: weightConversions(useImperialUnits),
    temperature: temperatureConversion(useImperialUnits),
    oxygen_saturation: {
      units: 'percent',
      conversion: (value) => value,
    },
    respiratory_rate: {
      units: 'breaths_per_minute',
      conversion: (value) => value,
    },
    heart_rate: {
      units: 'beats_per_minute',
      conversion: (value) => value,
    },
    respiratory_rate_percentile: {
      units: 'percent',
      conversion: (value) => value,
    },
    weight_percentile: {
      units: 'percent',
      conversion: (value) => value,
    },
    height: heightConversion(useImperialUnits),
    heart_rate_percentile: {
      units: 'percent',
      conversion: (value) => value,
    },
    followup_days_absent: {
      units: 'days',
      conversion: (value) => value,
    },
    training_duration: {
      units: 'minutes',
      conversion: (value) => value,
    },
    training_attendees: {
      units: 'people',
      conversion: (value) => value,
    },
    coughing_duration: {
      units: 'days',
      conversion: (value) => value,
    },
    followup_last_encounter: {
      units: 'days',
      conversion: (value) => value,
    },
    vomiting_frequency: {
      units: 'times',
      conversion: (value) => value,
    },
    diarrhea_frequency: {
      units: 'times',
      conversion: (value) => value,
    },
    last_urination: {
      units: 'hours',
      conversion: (value) => value,
    },
    wet_diapers: {
      units: 'hours',
      conversion: (value) => value,
    },
    encounter_previous_visits: {
      units: 'times',
      conversion: (value) => value,
    },
    encounter_previous_visits_anc: {
      units: 'times',
      conversion: (value) => value,
    },
    encounter_previous_visits_pnc: {
      units: 'times',
      conversion: (value) => value,
    },
    pregnancy_days: {
      units: 'days',
      conversion: (value) => value,
    },
    fever_duration: {
      units: 'days',
      conversion: (value) => value,
    },
    diarrhea_duration: {
      units: 'days',
      conversion: (value) => value,
    }
  })

  export default {
    name: 'List',
    mixins: [ageRanges, displayFormats, languageLogic, icons, tokens, ui],
    props: ['item'],
    data() {
      return {
        showAbbreviation: false,
        nonMetricSystem: null,
        track: null
      }
    },
    computed: {
      badge() {
        return this.item.name === 'weight' ? { color: 'red', icon: 'priority_high' } : { color: 'transparent', icon: '' }
      },
      abbreviation() { return this.$t('questions.' + this.item.name + '.title').charAt(0).toUpperCase() },
      name() { return 'questions.' + this.item.name + '.title' },
      labels() {
        return this.selectedResponses().map(response => {
          return `responses.${response.name}.title`;
        })
      },
      selectedResponses() {
        return this.item.responses.filter(response => response.selected)
      },
      titleId() {
        return `${this.item.name}-title`
      }
    },
    methods: {
      editResponse() {
        if (this.item.editRoutePath) {
          this.$router.push({
            path: this.item.editRoutePath,
          }).catch(error => {
            if (error.name !== 'NavigationDuplicated') throw error
          });
          return;
        }

        const tracks_id = this.$route.params.tracks_id

        let params = {
          panels_name: this.item.name,
        }

        if (tracks_id) {
          params.tracks_id = tracks_id
          this.$store.commit('v4/track/set', { editResponsesMode: true })
        }

        this.$router.push({
          name: capitalize(this.$route.meta.collection),
          params: params,
        }).catch(error => {
          if (error.name !== 'NavigationDuplicated') throw error
        })
      },
      valueWithUnitsDisplayString(value) {
        const conversions = getConversions(this.nonMetricSystem === 'US')
        const conversion = conversions[this.item.name]
        const convertedValue = this.$i18n.n(conversion.conversion(value), 'decimal')
        const displayUnit = conversion.units
        const units = this.$t(`units.${displayUnit}.title`)
        return `${convertedValue} ${units}`
      },
      generateDisplayString(response) {
        let retString = ''

        let value = isNaN(this.$i18n.n(response.value, 'decimal')) ? response.value : this.$i18n.n(response.value, 'decimal')

        try {
          let temp = [...response.value].map(c => this.$i18n.n(c, 'decimal'))
          if (temp.every(x => { return !isNaN(x) })) {
            value = temp.join('')
          }
        }
        catch (e) {}

        if (this.item.name === 'age') {
          retString = this.ageDisplayString(value)
        }
        else if (['slider', 'tapper'].includes(this.item.input)) {
          retString = this.valueWithUnitsDisplayString(value)
        }
        else if (this.item.name === 'baby_age_display') {
          retString = this.days(value)
        }
        else {
          retString = value
        }
        return retString
      },
      formatItemTypeIsValue(response) {
        let retString = ''
        if (this.item.responses[0].unknown === true) {
          retString = 'app.inputs.unknown.display'
        }
        else {
          retString = this.generateDisplayString(response)
        }
        return retString
      },
      formatDate(response) {
        return (response.unknown) ? 'app.inputs.unknown.display' : this.dateFromIso(response.value)
      },
      formatResponse(response) {
        let retString = ''

        switch(this.item.type) {
          case 'value':
            retString = this.formatItemTypeIsValue(response)
            break
          case 'date':
            retString = this.formatDate(response)
            break
          case 'location':
            retString = response.token;
            break
          default:
            retString = this.responseToken(response)
            break
        }
        return retString
      },
      responseId(response) {
        const nameSplit = response.name.split('.')
        return `${nameSplit[0]}-${nameSplit[1]}`
      },
    },
    created() {
      this.track = this.$route.params.track
    }
  }
</script>

<style scoped>
  .list-card-title {
    font-size: 16px;
    font-weight: 600;
  }

  .list-card-subtitle {
    font-size: 14px;
    font-weight: 400;
    color: var(--dark-grey) !important;
  }
</style>
